import React, { useState, useEffect } from 'react';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { CATEGORY_CODE, PAGE, PAGE_CODE } from '../../../lib/pageCodeDefaults';
import { SLIDEOUTS as MP_SLIDEOUTS } from '../../Slideout/Slideouts';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import DropdownViews from 'ps-components/Dropdown/DropdownViews';
import JobEmployeesGrid from './JobEmployeesGrid/JobEmployeesGrid';
import Pagination from 'ps-components/Pagination/Pagination';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import { jobEmployeesGridActions } from './JobEmployeesGrid/JobEmployeesGridCreate';
import {
  getEmployeeSelectedView,
  getEmployeeViews,
  getJobEmployees,
  setEmployeeSelectedView
} from './JobEmployeesActions';
import './JobEmployees.scss';

function JobEmployees(props) {
  const {
    actions,
    employeeViews,
    grid,
    hideSlideout,
    jobCode,
    jobCodeKey,
    jobEmployeesData,
    loading,
    selectedViewPk,
    showSlideout,
    showJobCodeKey
  } = props;
  const limit = grid.limit;
  const offset = grid.offset;

  useEffect(() => {
    actions.getEmployeeViews();
  }, []);

  useEffect(() => {
    const options = {
      limit,
      offset,
      jobCode,
      jobCodeKey,
      viewPk: selectedViewPk,
      orderBy: grid.orderBy
    };

    actions.getJobEmployees(options);
  }, [selectedViewPk, grid.orderBy]);

  function getViewDropdown() {
    return (
      <div className="job-employees__dropdown-group">
        <label>Employee View</label>
        <Dropdown
          className="job-employees__dropdown"
          customRenderComponent={DropdownViews}
          customRenderProps={{
            onEdit: showConfigureViewSlideout,
            onSelectView: setSelectedEmployeePageView,
            selectedViewPk: selectedViewPk,
            views: employeeViews,
            userRights: props.userRights,
            groupAdminCanConfigureViews: props.groupAdminCanConfigureViews,
            regularUserCanConfigureViews: props.regularUserCanConfigureViews
          }}
          text={getSelectedViewName(selectedViewPk)}
        />
      </div>
    );
  }

  function getSelectedViewName(viewPk) {
    const selectedView = employeeViews?.find(v => v.view_pk === viewPk);

    return grid?.metadata?.viewName || selectedView?.view_name;
  }

  function pageEmployees(limit, offset) {
    const options = {
      limit,
      offset,
      jobCode,
      jobCodeKey,
      viewPk: selectedViewPk
    };

    actions.getJobEmployees(options);
    actions.pageList(limit, offset);
  }

  function refreshViews() {
    actions.getEmployeeViews().then(() => {
      actions.getEmployeeSelectedView();
    });
  }

  function setSelectedEmployeePageView(view) {
    const selectedView = employeeViews?.find(l => l.view_name?.trim() === view?.trim());

    if (selectedView) {
      actions.setEmployeeSelectedView(selectedView.view_pk);
    }
  }

  function showConfigureViewSlideout() {
    showSlideout(MP_SLIDEOUTS.CONFIGURE_VIEW, {
      categoryCode: CATEGORY_CODE.JOB_EMPLOYEES,
      includeAggregates: false,
      isGroupingEnabled: false,
      navigateToPrevSlideout: showJobEmployeesSlideout,
      onRefresh: refreshViews,
      page: PAGE.JOB_EMPLOYEES,
      pageCode: PAGE_CODE.JOB_EMPLOYEES,
      pageName: 'Employee Views'
    });
  }

  function showJobEmployeesSlideout() {
    showSlideout(MP_SLIDEOUTS.JOB_EMPLOYEES, {
      jobCode,
      jobCodeKey
    });
  }

  return (
    <div className="job-employees__container">
      <SlideoutHeader doClose={() => hideSlideout()} title="View Employees" />
      <div className="job-employees__sub-header">
        <h3 className="job-employees__title">Employee Data</h3>
        {getViewDropdown()}
      </div>
      <div className="generic-grid__container job-employees__grid-container">
        <JobEmployeesGrid
          jobEmployeesGrid={grid}
          jobEmployeesData={jobEmployeesData}
          loading={loading}
          onSortChange={actions.onSortChange}
          showJobCodeKey={showJobCodeKey}
        />
      </div>
      <div className="job-employees__pagination-container">
        <Pagination
          className="job-employees__pagination"
          offset={offset}
          pageAction={pageEmployees}
          step={limit}
          totalCount={jobEmployeesData?.metadata?.totalCount}
        />
      </div>
    </div>
  );
}

JobEmployees.propTypes = {
  actions: shape({
    getEmployeeSelectedView: func,
    getEmployeeViews: func,
    getJobEmployees: func,
    setEmployeeSelectedView: func
  }),
  employeeViews: arrayOf(object),
  grid: object,
  hideSlideout: func,
  jobCode: string,
  jobCodeKey: string,
  jobEmployeesData: shape({
    data: arrayOf(array),
    metadata: object,
    terms: arrayOf(object)
  }),
  loading: bool,
  selectedViewPk: number,
  showSlideout: func,
  userRights: object,
  groupAdminCanConfigureViews: bool,
  regularUserCanConfigureViews: bool
};

const mapStateToProps = ({ jobEmployees, session }) => {
  return {
    employeeViews: jobEmployees.JobEmployeesMainReducer.employeeViews?.views,
    grid: jobEmployees.JobEmployeesGridReducer,
    jobEmployeesData: jobEmployees.JobEmployeesMainReducer.jobEmployeesData,
    loading: jobEmployees.JobEmployeesMainReducer.loading,
    selectedViewPk: jobEmployees.JobEmployeesMainReducer.employeeSelectedView?.selectedViewPk,
    userRights: session.user.rights,
    groupAdminCanConfigureViews: session.groupAdminCanConfigureViews,
    regularUserCanConfigureViews: session.regularUserCanConfigureViews,
    showJobCodeKey: session.organization.useJobCodeKey
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      getEmployeeSelectedView,
      getEmployeeViews,
      getJobEmployees,
      onSortChange: jobEmployeesGridActions.onSortChange,
      pageList: jobEmployeesGridActions.pageList,
      setEmployeeSelectedView
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(JobEmployees);
